import React from 'react';

interface SafeExternalLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
}

/**
 * Componente para enlaces externos seguros con protección SEO
 * 
 * CARACTERÍSTICAS:
 * - rel="noopener noreferrer nofollow" por defecto
 * - target="_blank" para abrir en nueva pestaña
 * - Protege PageRank y Link Juice
 * - Previene ataques de seguridad
 * 
 * USO:
 * <SafeExternalLink href="https://facebook.com/neuropequescr">
 *   <FacebookIcon />
 * </SafeExternalLink>
 */
export const SafeExternalLink: React.FC<SafeExternalLinkProps> = ({
  href,
  children,
  className = '',
  ariaLabel,
  target = '_blank',
  rel = 'noopener noreferrer nofollow',
  onClick
}) => {
  // Validar que sea un enlace externo
  const isExternal = href.startsWith('http://') || href.startsWith('https://');
  
  // Si no es externo, usar un enlace normal
  if (!isExternal) {
    return (
      <a 
        href={href} 
        className={className}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      className={className}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </a>
  );
};

/**
 * Componente especializado para redes sociales
 * 
 * CARACTERÍSTICAS:
 * - Rel="nofollow" para proteger PageRank
 * - Estilos consistentes para redes sociales
 * - Accesibilidad mejorada
 */
export const SocialMediaLink: React.FC<{
  href: string;
  children: React.ReactNode;
  platform: 'facebook' | 'instagram' | 'linkedin' | 'twitter' | 'youtube';
  className?: string;
  onClick?: () => void;
}> = ({ href, children, platform, className = '', onClick }) => {
  const getAriaLabel = () => {
    const platformNames = {
      facebook: 'Facebook',
      instagram: 'Instagram',
      linkedin: 'LinkedIn',
      twitter: 'Twitter',
      youtube: 'YouTube'
    };
    
    return `Visitar nuestro perfil de ${platformNames[platform]} (se abre en nueva ventana)`;
  };

  return (
    <SafeExternalLink
      href={href}
      className={className}
      ariaLabel={getAriaLabel()}
      onClick={onClick}
    >
      {children}
    </SafeExternalLink>
  );
};

/**
 * Componente para enlaces de contacto externos
 * 
 * CARACTERÍSTICAS:
 * - Rel="nofollow" para enlaces de contacto
 * - Mantiene funcionalidad pero protege SEO
 * - Ideal para enlaces de WhatsApp, email, teléfono
 */
export const ContactExternalLink: React.FC<{
  href: string;
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  onClick?: () => void;
}> = ({ href, children, className = '', ariaLabel, onClick }) => {
  return (
    <SafeExternalLink
      href={href}
      className={className}
      ariaLabel={ariaLabel}
      rel="noopener noreferrer nofollow"
      onClick={onClick}
    >
      {children}
    </SafeExternalLink>
  );
};
